import {
  type ComponentType,
  createElement,
  lazy,
  type ReactElement,
} from 'react';

import {
  isStaleChunkError,
  noteChunkLoaded,
  pendingUntilReload,
  recoverFromChunkFailure,
  recoverFromNewBuild,
} from '~/utils/staleChunk';

export type RouteModule = Record<string, unknown>;
export type RouteLoader = () => Promise<RouteModule>;
/** Picks the component out of a module that does not default-export it. */
export type ModulePick<P> = (module: RouteModule) => ComponentType<P>;

function pickDefault<P>(module: RouteModule) {
  return module.default as ComponentType<P>;
}

/**
 * Turns a module that does not export what the caller asked for into an error
 * that names it. Without this React only reports "Element type is invalid" from
 * whichever route happened to render, with no trace back to the manifest entry.
 */
function assertPicked<P>(
  component: ComponentType<P> | undefined,
  moduleId: string,
): ComponentType<P> {
  if (!component) {
    throw new Error(
      `lazyComponent: "${moduleId}" does not export the component the manifest asks for — add a default export or pass a pick`,
    );
  }
  return component;
}

/**
 * A component backed by its own JS/CSS chunk.
 *
 * Once `preload()` has resolved, the component renders synchronously. That
 * matters because SSR goes through `renderToString`, which cannot wait on a
 * pending `React.lazy` and would otherwise emit the Suspense fallback instead
 * of the page — costing SEO content and producing a hydration mismatch.
 * Before preload resolves (client-side navigation to a route the user has not
 * visited yet) it falls back to a regular lazy component under Suspense.
 */
export interface LazyComponent<P> {
  (props: P): ReactElement;
  /** Loads the chunk and makes subsequent renders synchronous. */
  preload: () => Promise<ComponentType<P>>;
}

export interface LazyOptions<P> {
  pick?: ModulePick<P>;
  /**
   * Whether a chunk that fails over a healthy build may reload the page.
   *
   * True for a route, whose absence is a dead screen with nothing left to do.
   * False for anything rendered beside the route — a modal that fails can be
   * reported in place, and reloading for it would throw away what the page
   * still holds: a filled checkout form, a typed promo code, a signing flow
   * mid-poll.
   */
  reloadToRecover?: boolean;
}

/**
 * Wraps a dynamic import into a preloadable component.
 */
export function lazyComponent<P extends object>(
  moduleId: string,
  loader: RouteLoader,
  { pick = pickDefault, reloadToRecover = false }: LazyOptions<P> = {},
): LazyComponent<P> {
  let resolved: ComponentType<P> | null = null;

  const load = async (): Promise<ComponentType<P>> => {
    try {
      const component = assertPicked(pick(await loader()), moduleId);
      // The chunk arrived, so whatever went wrong before is over and the
      // recovery budget belongs to the next incident, not to this one.
      noteChunkLoaded();
      return component;
    } catch (error) {
      if (!isStaleChunkError(error)) {
        throw error;
      }

      // A deploy is the failure a reload provably fixes, so it is asked about
      // first; a dropped connection is the one left over, and a reload is the
      // only thing that undoes it either way. Retrying the loader here would
      // not: the browser records a module it failed to fetch as failed, and
      // returns that same error for the same URL without a network request.
      // That probe doubles as the only trustworthy evidence that the network
      // answers at all, which the second recovery needs before it reloads.
      const { reloading, networkIsUp } = await recoverFromNewBuild();

      if (
        reloading ||
        (reloadToRecover && recoverFromChunkFailure(networkIsUp))
      ) {
        // A reload is under way. Never settling keeps the page the user is
        // looking at on screen until it lands, instead of tearing it down for
        // an error state that is about to be thrown away anyway.
        return await pendingUntilReload<ComponentType<P>>();
      }
      throw error;
    }
  };

  const Suspended = lazy(async () => ({
    default: await load(),
  })) as unknown as ComponentType<P>;

  const Component = (props: P) => createElement(resolved ?? Suspended, props);
  Component.displayName = `Lazy(${moduleId})`;

  Component.preload = async () => {
    resolved = await load();
    return resolved;
  };

  return Component;
}

type NoProps = Record<string, never>;

export type RoutePick = ModulePick<NoProps>;
export type LazyRoute = LazyComponent<NoProps>;

/**
 * `lazyComponent` for route pages, which take no props.
 *
 * A route is the one chunk worth reloading the page for: without it there is
 * nothing on screen to preserve.
 */
export function lazyRoute(
  moduleId: string,
  loader: RouteLoader,
  pick?: RoutePick,
): LazyRoute {
  return lazyComponent<NoProps>(moduleId, loader, {
    pick,
    reloadToRecover: true,
  });
}
